import envConfig from './env'
import type { EnvConfig } from './env'

/**
 * 应用全局配置接口
 */
export interface AppConfig extends EnvConfig {
  // 应用名称
  appName: string
  // 应用版本
  appVersion: string
  // 应用主题 - 'light' | 'dark' | 'system'
  theme: 'light' | 'dark' | 'system'
  // 默认语言
  defaultLang: string
  // 是否开启权限验证
  enablePermission: boolean
  // HTTP请求超时时间(毫秒)
  timeout: number
  // HTTP请求重试次数
  retryTimes: number
  // HTTP请求重试延迟(毫秒)
  retryDelay: number
  // 请求并发控制数
  maxConcurrent: number
  // token存储key
  tokenKey: string
  // token过期时间(天)
  tokenExpires: number
  // 是否自动存储页面状态
  cachePageState: boolean
  // 是否允许多设备登录
  allowMultiDeviceLogin: boolean
  // 租户id
  staff_id: string
}

/**
 * 默认配置
 */
const defaultConfig: Omit<AppConfig, keyof EnvConfig> = {
  appName: '楼小帮',
  appVersion: '1.0.0',
  theme: 'light',
  defaultLang: 'zh-CN',
  enablePermission: true,
  timeout: 10000,
  retryTimes: 3,
  retryDelay: 1000,
  maxConcurrent: 10,
  tokenKey: 'access_token',
  tokenExpires: 7,
  cachePageState: true,
  allowMultiDeviceLogin: false,
  staff_id: '1'
}

// 合并环境配置和应用配置
const config = {
  ...defaultConfig,
  ...envConfig
} as AppConfig

/**
 * 获取配置
 * @param key 配置键
 * @returns 配置值
 */
export function getConfig<T extends keyof AppConfig>(key: T): AppConfig[T] {
  return config[key]
}

/**
 * 获取完整配置
 * @returns 完整配置对象
 */
export function getAllConfig(): AppConfig {
  return config
}

/**
 * 动态设置配置
 * @param key 配置键
 * @param value 配置值
 */
export function setConfig<T extends keyof AppConfig>(key: T, value: AppConfig[T]): void {
  config[key] = value

  // 如果有必要，这里可以添加配置变更后的处理逻辑
  // 例如：存储到localStorage等
  if (['theme', 'defaultLang'].includes(key as string)) {
    try {
      uni.setStorageSync(`APP_CONFIG_${key}`, value)
    } catch (error) {
      console.error(`保存配置失败: ${error}`)
    }
  }
}

// 初始化配置
function initConfig(): void {
  // 从本地存储加载配置
  try {
    // 加载主题设置
    const theme = uni.getStorageSync('APP_CONFIG_theme')
    if (theme) {
      config.theme = theme as 'light' | 'dark' | 'system'
    }

    // 加载语言设置
    const lang = uni.getStorageSync('APP_CONFIG_defaultLang')
    if (lang) {
      config.defaultLang = lang
    }

    // 可以添加其他需要从存储中恢复的配置项
  } catch (error) {
    console.error(`加载配置失败: ${error}`)
  }
}

// 初始化配置
initConfig()

// 默认导出完整配置
export default config
